import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight,
  MessageSquareText,
  Zap,
  Clock,
  BadgeCheck,
  RefreshCw,
} from "lucide-react";

const LEO_SCRIPT = [
  { from: "cust", text: "Hey, do you guys do house washing? Looking for a quote." },
  { from: "leo", text: "Hey! I'm LEO from The 7 Figure Cleaner team. Happy to help — is it a single or double storey?" },
  { from: "cust", text: "Single storey. 4-bed brick." },
  { from: "leo", text: "Nice. And roughly what's the street address?" },
  { from: "cust", text: "42 Campbell Pde, Bondi NSW." },
  { from: "leo", text: "Perfect — you're inside our service area. Want the team to send a quote today?" },
  { from: "cust", text: "Yes please." },
  { from: "leo", text: "Locked in. Someone from the team will be in touch within the hour. 🙌" },
];

const TYPING_MS = 700;
const READ_MS = 1300;

const LeoChat = () => {
  const [visible, setVisible] = useState([]);
  const [typing, setTyping] = useState(null);
  const scrollerRef = useRef(null);
  const timeoutsRef = useRef([]);

  useEffect(() => {
    let cancelled = false;
    const queue = (fn, delay) => {
      const id = setTimeout(() => {
        if (!cancelled) fn();
      }, delay);
      timeoutsRef.current.push(id);
    };
    const run = () => {
      setVisible([]);
      setTyping(null);
      let cursor = 400;
      LEO_SCRIPT.forEach((msg, i) => {
        queue(() => setTyping(msg.from), cursor);
        cursor += TYPING_MS;
        queue(() => {
          setTyping(null);
          setVisible((v) => [...v, i]);
        }, cursor);
        cursor += READ_MS;
      });
      queue(() => {
        if (!cancelled) run();
      }, cursor + 1800);
    };
    run();
    return () => {
      cancelled = true;
      timeoutsRef.current.forEach(clearTimeout);
      timeoutsRef.current = [];
    };
  }, []);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  }, [visible, typing]);

  return (
    <div className="relative overflow-hidden rounded-[28px] bg-gradient-to-b from-blue-50 to-white p-1.5 ring-1 ring-brand-blue/15 shadow-xl shadow-blue-200/40">
      <div className="rounded-[22px] bg-white ring-1 ring-gray-200">
        {/* Chat header with Leo avatar */}
        <div className="flex items-center justify-between gap-3 border-b border-gray-100 px-4 py-3">
          <div className="flex items-center gap-3">
            <img
              src="/logo/round-md.webp"
              alt="Leo, your AI teammate"
              width="40"
              height="40"
              className="h-10 w-10 shrink-0 rounded-full ring-1 ring-brand-blue/25 shadow-sm"
              loading="lazy"
            />
            <div className="flex flex-col">
              <span className="text-sm font-black uppercase tracking-wide text-brand-black">
                Leo · AI Teammate
              </span>
              <span className="inline-flex items-center gap-1.5 text-[11px] font-bold text-emerald-600">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                Online · Replies in seconds
              </span>
            </div>
          </div>
          <span className="hidden text-[10px] font-black uppercase tracking-widest text-brand-grayMid sm:inline">
            SMS
          </span>
        </div>

        {/* Scrollable conversation */}
        <div
          ref={scrollerRef}
          className="hide-scrollbar flex h-[340px] flex-col gap-2 overflow-y-auto px-4 py-4 sm:h-[380px]"
        >
          <AnimatePresence initial={false}>
            {visible.map((i) => {
              const msg = LEO_SCRIPT[i];
              const isLeo = msg.from === "leo";
              return (
                <motion.div
                  key={`m-${i}`}
                  initial={{ opacity: 0, y: 8, scale: 0.96 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  transition={{ duration: 0.28, ease: "easeOut" }}
                  className={`max-w-[85%] rounded-2xl px-3 py-2 text-[12px] leading-relaxed shadow-sm sm:text-sm ${
                    isLeo
                      ? "self-end rounded-br-sm bg-brand-blue text-white"
                      : "self-start rounded-bl-sm bg-gray-100 text-brand-black"
                  }`}
                >
                  {msg.text}
                </motion.div>
              );
            })}
            {typing && (
              <motion.div
                key={`typing-${typing}`}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className={`flex items-center gap-1 rounded-2xl px-3 py-2 ${
                  typing === "leo"
                    ? "self-end rounded-br-sm bg-brand-blue/90"
                    : "self-start rounded-bl-sm bg-gray-100"
                }`}
              >
                {[0, 1, 2].map((d) => (
                  <motion.span
                    key={d}
                    className={`block h-1.5 w-1.5 rounded-full ${
                      typing === "leo" ? "bg-white/90" : "bg-brand-grayMid"
                    }`}
                    animate={{ y: [0, -3, 0], opacity: [0.4, 1, 0.4] }}
                    transition={{
                      duration: 0.9,
                      repeat: Infinity,
                      ease: "easeInOut",
                      delay: d * 0.15,
                    }}
                  />
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

const FEATURES = [
  {
    Icon: Zap,
    title: "Responds in Seconds",
    text: "LEO contacts every new enquiry immediately — day, night and weekends.",
  },
  {
    Icon: RefreshCw,
    title: "Follows Up Automatically",
    text: "If someone doesn't respond straight away, LEO continues the conversation so fewer opportunities are forgotten.",
  },
  {
    Icon: MessageSquareText,
    title: "Collects the Job Details",
    text: "LEO asks about the service, location, property, timeframe and other information you need before quoting.",
  },
  {
    Icon: BadgeCheck,
    title: "Hands the Opportunity Over",
    text: "Once the details are collected, LEO sends the conversation and job details directly to you or your team.",
  },
  {
    Icon: RefreshCw,
    title: "Reactivates Old Opportunities",
    text: "LEO reconnects with previous enquiries that never booked and brings interested homeowners back into your pipeline.",
  },
];

const MeetLeo = () => {
  return (
    <section id="leo" className="scroll-mt-20 bg-gradient-to-b from-white to-blue-50/40 py-14 sm:py-20">
      <div className="mx-auto max-w-6xl px-5 sm:px-8">
        <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-12 lg:gap-14">
          {/* LEFT — copy */}
          <div className="lg:col-span-7">
            <p className="mb-4 inline-flex items-center gap-2 rounded-full bg-emerald-100 px-3.5 py-1.5 text-xs font-black uppercase tracking-widest text-emerald-800">
              <Clock className="h-3.5 w-3.5" /> Your 24/7 AI Follow-Up Teammate
            </p>
            <h2 className="text-3xl font-black uppercase leading-[1.05] tracking-tight text-brand-black sm:text-4xl lg:text-5xl">
              Meet <span className="text-brand-blue">LEO</span> — The AI That Turns Enquiries Into Quote Opportunities.
            </h2>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-[#374151] sm:text-lg">
              Generating an enquiry is only the beginning. LEO responds within seconds, follows up automatically, asks the right questions and sends the details directly to you or your team.
            </p>
            <p className="mt-3 max-w-xl text-base leading-relaxed text-[#374151] sm:text-lg">
              While you&apos;re working, driving or quoting another job, LEO keeps the conversation moving.
            </p>

            <ul className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2">
              {FEATURES.map(({ Icon, title, text }) => (
                <li
                  key={title}
                  className="rounded-2xl bg-white p-4 shadow-sm ring-1 ring-gray-200 transition-all hover:shadow-md hover:ring-brand-blue/30 sm:p-5"
                >
                  <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-brand-blue/10 text-brand-blue ring-1 ring-brand-blue/20">
                    <Icon className="h-4 w-4" />
                  </span>
                  <h3 className="mt-3 text-sm font-black uppercase tracking-wide text-brand-black">
                    {title}
                  </h3>
                  <p className="mt-1.5 text-xs leading-relaxed text-brand-grayMid sm:text-sm">
                    {text}
                  </p>
                </li>
              ))}
            </ul>

            <div className="mt-8 flex flex-col items-stretch gap-3 sm:flex-row sm:items-center">
              <Link
                to="/offer"
                className="inline-flex min-h-[56px] items-center justify-center gap-2 rounded-2xl bg-brand-blue px-7 py-4 text-sm font-black uppercase tracking-wider text-white shadow-xl shadow-blue-200/60 ring-1 ring-blue-700/10 transition-all hover:bg-blue-600 active:scale-[0.99]"
              >
                Put LEO To Work In My Territory <ArrowRight className="h-5 w-5" />
              </Link>
              <span className="text-xs font-black uppercase tracking-widest text-brand-grayMid">
                Included in every plan. No extra fees.
              </span>
            </div>
          </div>

          {/* RIGHT — animated SMS demo */}
          <div className="lg:col-span-5">
            <LeoChat />
          </div>
        </div>
      </div>
    </section>
  );
};

export default MeetLeo;
